import { useVesselPath } from "@/lib/hooks";
import { Link } from "react-router-dom";

import type { ReadingDue } from "@/types";

/**
 * "Test the water" as a step, not as a scolding.
 *
 * It sits first in the same ordered list as the dose suggestions, alongside
 * `RetestCard`, and for the same reason: when the app cannot vouch for the
 * chlorine, testing genuinely *is* the first thing to do, and the heading above
 * already promises order. The two never appear together — `RetestCard` covers
 * the case where something was added since the test, this one covers the case
 * where nothing was and time alone has run the cover out.
 *
 * **Every figure here was measured.** The card says what the chlorine was, what
 * the floor is, and what fall this pool has actually recorded — and stops. It
 * never prints where the chlorine is *now*, even though the arithmetic behind
 * the card had to estimate it to decide the cover was spent. A projected ppm on
 * a dashboard is one somebody doses against, and that is the line ADR 0014 draws
 * to keep this feature on the safe side of non-negotiable #1.
 *
 * The evidence is shown rather than summarised because a reminder whose
 * reasoning is invisible is one that gets dismissed on the third day. "Test the
 * water" earns nothing; "17.0 against a 12.5 floor, and this pool has dropped
 * 5.5 in a day" earns the trip.
 */
export default function ReadingDueCard({ due }: { due: ReadingDue }) {
  const vp = useVesselPath();
  return (
    <li className="card p-4">
      <p className="text-sm font-medium">Test free chlorine</p>
      {/* Verdict, then working — the same order the saturation card uses, and
          the reason the server sends the two apart. */}
      <p className="mt-1 text-sm">{due.headline}</p>
      <p className="mt-1 text-sm text-(--color-ink-muted)">{due.note}</p>

      <dl className="mt-3 space-y-1.5 text-sm">
        <Row label="Last measured">
          <span className="figure">{due.fc}</span> ppm
        </Row>
        <Row label={`Floor at CYA ${due.cya}`}>
          <span className="figure">{due.floor}</span> ppm
        </Row>
        {/* The rate is the working, so it is stated as what it is — a figure
            derived from a measurement — rather than presented as one. */}
        <Row label={due.observed ? "Measured fall" : "Chart allowance"}>
          {due.observed ? (
            <>
              <span className="figure">{due.observed.ppm}</span> ppm over{" "}
              <span className="figure">{due.observed.solar_days}</span> days of sun
            </>
          ) : (
            <>
              <span className="figure">{due.rate_ppm_per_day}</span> ppm per day
            </>
          )}
        </Row>
      </dl>

      <Link to={vp("/log")} className="btn btn-primary mt-3 w-full text-sm">
        Log a reading
      </Link>
    </li>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="min-w-0 truncate text-(--color-ink-muted)">{label}</dt>
      <dd className="shrink-0">{children}</dd>
    </div>
  );
}
